// Client-side sync config (Worker URL + shared secret). Stored in IndexedDB (the
// `meta` table) alongside the session data so credentials survive together. The
// original home was localStorage, but iOS evicts localStorage far more aggressively
// than IndexedDB: the credentials vanished while the sessions survived, which made
// isSyncConfigured() silently return false and stranded finished sessions in the
// outbox unsynced (2026-06-14). A one-time migration lifts any pre-existing
// localStorage values into IndexedDB.
//
// getSyncConfig()/isSyncConfigured() must stay synchronous (flushOutbox early-returns
// on them inside hot paths), so values are mirrored into an in-memory cache that
// hydrateSyncConfig() fills at boot, before the first sync attempt.

import { getMeta, setMeta } from '../db/repo';

const META_URL = 'sync.workerUrl';
const META_SECRET = 'sync.secret';
const META_LAST_PULL = 'sync.lastCoachingPull';
const META_LAST_SYNC = 'sync.lastSyncAt';

// Legacy localStorage keys (pre-2026-06-14). Read once for migration, then ignored.
const LS_URL = 'sync.workerUrl';
const LS_SECRET = 'sync.secret';
const LS_LAST_PULL = 'sync.lastCoachingPull';

export interface SyncConfig {
  workerUrl: string;
  secret: string;
}

let cache: SyncConfig = { workerUrl: '', secret: '' };
let hydrated = false;

function lsGet(key: string): string {
  if (typeof localStorage === 'undefined') return '';
  return localStorage.getItem(key)?.trim() ?? '';
}

// Load config from IndexedDB into the in-memory cache, migrating any legacy
// localStorage values on first run. Call once at app boot before syncNow().
export async function hydrateSyncConfig(): Promise<void> {
  let url = (await getMeta(META_URL))?.trim() ?? '';
  let secret = (await getMeta(META_SECRET))?.trim() ?? '';

  // Migrate from localStorage when IndexedDB has nothing yet but localStorage does.
  if (!url) {
    const lsUrl = lsGet(LS_URL);
    if (lsUrl) { url = lsUrl; await setMeta(META_URL, url); }
  }
  if (!secret) {
    const lsSecret = lsGet(LS_SECRET);
    if (lsSecret) { secret = lsSecret; await setMeta(META_SECRET, secret); }
  }
  if (!(await getMeta(META_LAST_PULL))) {
    const lsPull = lsGet(LS_LAST_PULL);
    if (lsPull) await setMeta(META_LAST_PULL, lsPull);
  }

  cache = { workerUrl: url, secret };
  hydrated = true;
}

export function getSyncConfig(): SyncConfig {
  return { ...cache };
}

export async function setSyncConfig(cfg: SyncConfig): Promise<void> {
  const workerUrl = cfg.workerUrl.trim();
  const secret = cfg.secret.trim();
  await setMeta(META_URL, workerUrl);
  await setMeta(META_SECRET, secret);
  cache = { workerUrl, secret };
  hydrated = true;
}

export function isSyncConfigured(): boolean {
  return cache.workerUrl.length > 0 && cache.secret.length > 0;
}

export function isSyncHydrated(): boolean {
  return hydrated;
}

export async function getLastCoachingPull(): Promise<string | null> {
  return (await getMeta(META_LAST_PULL)) ?? null;
}

export async function setLastCoachingPull(iso: string): Promise<void> {
  await setMeta(META_LAST_PULL, iso);
}

// Timestamp of the last fully successful round-trip to the Worker (no errors).
// Surfaced on the home screen + Settings so a finished session never leaves you
// guessing whether it actually reached Notion.
export async function getLastSyncAt(): Promise<string | null> {
  return (await getMeta(META_LAST_SYNC)) ?? null;
}

export async function setLastSyncAt(iso: string): Promise<void> {
  await setMeta(META_LAST_SYNC, iso);
}
